import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { generatePath } from 'react-router-dom';

import {
  clearApiKeys,
  requestAPI,
  resetReportsList,
} from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import {
  GET_ALL_REPORTS_PERIODS,
  GET_APP_CONFIGURATION,
  PDF_MERGE_PERIODS,
  REPORT_LIST_DATA_KEY,
} from 'src/redux/constants';
import { getPublicationListRequest } from 'src/redux/publicationStatus/actions';
import { getActionAPISource } from 'src/utils';
import { buildErrorComponents, showNotification } from 'src/utils';
import { DashboardType } from '../utils/reportsUtils';

export const PROJECT_STATUS: DashboardType = 'project';
export const REVIEW_STATUS: DashboardType = 'review';
export const PUBLICATION_STATUS: DashboardType = 'publication';

interface PeriodOption {
  value: string;
  label: string;
}

interface ReportListData {
  isFetching?: boolean;
  data?: any[] | null;
  error?: any;
}

interface ViewDataState {
  [key: string]: {
    data?: any;
    error?: any;
    isFetching?: boolean;
  };
}

interface RootState {
  viewData: ViewDataState;
}

interface UseReportsListDataReturn {
  listStatus: DashboardType;
  publicationStatusYears: string[];
  reportListData: ReportListData;
  allReportsPeriodsData: any;
  periods: PeriodOption[];
  currentPeriod: string | undefined;
  handlePeriodOnChange: (option: PeriodOption) => void;
  getPublicationList: (period?: string) => void;
  location: Location;
}

export const useReportsListData = (): UseReportsListDataReturn => {
  const location = useLocation();
  const params = useParams<{ year?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [publicationStatusYears, setPublicationStatusYears] = useState<
    string[]
  >([]);
  const [listStatus, setListStatus] = useState<DashboardType>(
    (location.hash ? location.hash.slice(1) : PROJECT_STATUS) as DashboardType,
  );

  const reportListData = useSelector(
    (state: RootState) => state.viewData?.[REPORT_LIST_DATA_KEY],
  ) || { isFetching: true, data: null, error: null };

  const allReportsPeriodsData = useSelector(
    (state: RootState) => state.viewData[GET_ALL_REPORTS_PERIODS],
  );

  const dispatchFetch = {
    [REPORT_LIST_DATA_KEY]: (dashboard_type: DashboardType) => {
      dispatch(
        requestAPI(REPORT_LIST_DATA_KEY, {
          data: {
            period: params.year || '',
            dashboard_type,
          },
        }),
      );
    },
    [GET_ALL_REPORTS_PERIODS]: () => {
      dispatch(requestAPI(GET_ALL_REPORTS_PERIODS, {}));
    },
  };

  useEffect(() => {
    if (!reportListData?.error) return;

    const errorsList = buildErrorComponents(reportListData?.error);
    showNotification(errorsList as any, 'error', {
      autoClose: false,
      closeOnClick: false,
    });
  }, [reportListData?.error]);

  useEffect(() => {
    const newListStatus = (
      location.hash ? location.hash.slice(1) : PROJECT_STATUS
    ) as DashboardType;
    changeListStatus(newListStatus);
  }, [location.hash]);

  useEffect(() => {
    const fetch = async () => {
      if (listStatus === 'project' || listStatus === 'review') {
        dispatchFetch[GET_ALL_REPORTS_PERIODS]();
      }

      if (listStatus === 'publication') {
        const publicationStatusYears = await getPublicationReportsYears();

        if (!publicationStatusYears) return;

        setPublicationStatusYears(
          [...publicationStatusYears].sort(
            (a: string, b: string) => parseInt(b) - parseInt(a),
          ),
        );
      }
    };
    fetch();
  }, [listStatus]);

  useEffect(() => {
    if (allReportsPeriodsData?.data || publicationStatusYears.length > 0) {
      enforceExistingPeriod(listStatus === 'publication');
    }
  }, [allReportsPeriodsData, publicationStatusYears, listStatus]);

  useEffect(() => {
    if (!params.year) return;

    if (listStatus === 'project' || listStatus === 'review') {
      dispatchFetch[REPORT_LIST_DATA_KEY](listStatus);
    }
    if (listStatus === 'publication') {
      getPublicationList(
        publicationStatusYears.indexOf(params.year) === -1
          ? undefined
          : params.year,
      );
    }
  }, [params.year, listStatus, publicationStatusYears]);

  useEffect(() => {
    dispatch(requestAPI(GET_APP_CONFIGURATION));
  }, [dispatch]);

  const getPublicationList = useCallback(
    (period?: string) => {
      dispatch(resetReportsList());
      dispatch(getPublicationListRequest({ period }));
    },
    [dispatch],
  );

  const flushReportsListData = useCallback(() => {
    dispatch(clearApiKeys([REPORT_LIST_DATA_KEY]));
  }, [dispatch]);

  const getPublicationReportsYears = useCallback(async (): Promise<
    string[] | null
  > => {
    try {
      const url = getActionAPISource(PDF_MERGE_PERIODS);
      const response = await apiCallAsync(url, null, 'get');
      return response?.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, []);

  const enforceExistingPeriod = useCallback(
    async (isPublication = false) => {
      const periods = isPublication
        ? publicationStatusYears
        : allReportsPeriodsData?.data;
      const yearParam = params.year;

      if (
        !!periods &&
        periods.length > 0 &&
        periods.indexOf(yearParam) === -1
      ) {
        navigate({
          pathname: generatePath('/:year?/', { year: periods[0] }) + '/',
          hash: location.hash ? location.hash.slice(1) : PROJECT_STATUS,
        });
      }
    },
    [
      publicationStatusYears,
      allReportsPeriodsData,
      params.year,
      navigate,
      location.hash,
    ],
  );

  const handlePeriodOnChange = useCallback(
    (option: PeriodOption) => {
      navigate({
        pathname: generatePath('/:year?/', { year: option.value }) + '/',
        hash: location.hash ? location.hash.slice(1) : PROJECT_STATUS,
      });
    },
    [navigate, location.hash],
  );

  const changeListStatus = useCallback(
    async (key: DashboardType) => {
      if (key === listStatus) return;
      flushReportsListData();
      setListStatus(key);
    },
    [listStatus, flushReportsListData],
  );

  const periods = allReportsPeriodsData?.data || [];
  const currentPeriod =
    periods.indexOf(params.year) === -1 ? undefined : params.year;

  const periodsOptions: PeriodOption[] =
    listStatus === 'publication'
      ? publicationStatusYears.map(i => ({ value: i, label: i }))
      : periods.map((i: string) => ({ value: i, label: i }));

  const publicationCurrentPeriod =
    publicationStatusYears.indexOf(params.year || '') === -1
      ? undefined
      : params.year;
  const finalCurrentPeriod =
    listStatus === 'publication' ? publicationCurrentPeriod : currentPeriod;

  return {
    listStatus,
    publicationStatusYears,

    reportListData,
    allReportsPeriodsData,

    periods: periodsOptions,
    currentPeriod: finalCurrentPeriod,

    handlePeriodOnChange,
    getPublicationList,

    location,
  };
};
